import { FitAddon } from "@xterm/addon-fit";
import { Terminal as XTerm } from "@xterm/xterm";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import "@xterm/xterm/css/xterm.css";
import { terminalStdout } from "@/context/terminalObservables";
import { RunButton } from "./RunButton";
import { useWasmer } from "@/context/useWasmer";
import { useEditorStateStore } from "@/context/editorStateStore";

const Terminal: React.FC = () => {
	const terminalTheme = {
		background: "#191919",
		foreground: "#eeeeee",
		cursor: "#eeeeee",
		selectionBackground: "#f66b15",
	};

	const terminalRef = useRef<HTMLDivElement>(null);
	const [xterm, setXterm] = useState<XTerm | null>(null);

	// Neded because of react StrictMode rendering things twice
	const xtermInitialized = useRef(false);

	const { runCommand, isLoading } = useWasmer();
	const { template } = useEditorStateStore();

	useEffect(() => {
		if (terminalRef.current && !xterm && !xtermInitialized.current) {
			const newXterm = new XTerm({
				convertEol: true,
				theme: terminalTheme,
			});
			const fitAddon = new FitAddon();
			newXterm.loadAddon(fitAddon);
			xtermInitialized.current = true;
			newXterm.open(terminalRef.current);
			fitAddon.fit();

			// Hide the internal xterm scrollbar
			const xtermViewport =
				terminalRef.current?.querySelector(".xterm-viewport");
			if (xtermViewport) {
				(xtermViewport as HTMLElement).style.overflow = "hidden";
			}

			setXterm(newXterm);
		}

		return () => {
			xterm?.dispose();
		};
	}, [xterm]);

	useEffect(() => {
		if (!xterm) return;
		const fitAddon = new FitAddon();
		xterm.loadAddon(fitAddon);
		const handleResize = () => {
			fitAddon.fit();
			const xtermViewport =
				terminalRef.current?.querySelector(".xterm-viewport");
			if (xtermViewport) {
				(xtermViewport as HTMLElement).style.overflow = "hidden";
			}
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [xterm]);

	useEffect(() => {
		const terminalStdoutSubscription = terminalStdout.subscribe({
			next: (v) => {
				xterm?.write(v.content);
			},
		});

		return () => {
			terminalStdoutSubscription.unsubscribe();
		};
	}, [xterm]);

	return (
		<div className="flex flex-col h-full w-full gap-2 p-2 bg-card">
			<div className="flex flex-row mx-auto mt-2">
				{isLoading && "loading"}
				<RunButton
					onClick={() => {
						const command = template.commands.run;
						if (xterm && command) {
							runCommand(command, xterm);
						}
					}}
				/>
			</div>
			<div className="flex-1 h-full" ref={terminalRef} />
		</div>
	);
};

export default Terminal;
